import React, { useEffect, useRef, useState } from "react";
import { Button, Card, CardBody, CardHeader, Label } from "reactstrap";
import AddFilter from "./AddFilter";

interface FilterProps {
	dataFields: any[];
	updateGroups: (groups: any[]) => void;
	groups: any[];
	t: (key: string) => string;
}

const Filter: React.FC<FilterProps> = ({ dataFields, updateGroups, groups: initialGroups, t }) => {
	const [currentFilter, setCurrentFilter] = useState<any>(null);
	const [filterGroups, setFilterGroups] = useState<any[]>([]);
	const [selectedGroupIndex, setSelectedGroupIndex] = useState<number | null>(null);
	const [selectedFilterIndex, setSelectedFilterIndex] = useState<number | null>(null);
	const prevFilterGroupsRef = useRef<any>();

	useEffect(() => {
		const init = initialGroups?.length ? initialGroups : [];
		if (JSON.stringify(init) !== JSON.stringify(prevFilterGroupsRef.current)) {
			setFilterGroups(init);
			prevFilterGroupsRef.current = JSON.parse(JSON.stringify(init));
		}
	}, [initialGroups]);


	useEffect(() => {
		if (JSON.stringify(filterGroups) !== JSON.stringify(prevFilterGroupsRef.current)) {
			updateGroups(filterGroups);
			prevFilterGroupsRef.current = JSON.parse(JSON.stringify(filterGroups));
		}
	}, [filterGroups]);

	const handleFilterSelection = (newFilter: any, groupIndex: number) => {
		const updatedGroups = [...filterGroups];
		if (selectedFilterIndex !== null && selectedGroupIndex !== null) {
			updatedGroups[selectedGroupIndex].filters[selectedFilterIndex] = newFilter;
		} else {
			updatedGroups[groupIndex].filters.push(newFilter);
		}
		setFilterGroups(updatedGroups);
		resetSelection();
	};

	const handleRemoveFilter = (groupIndex: number, filterIndex: number) => {
		const updatedGroups = [...filterGroups];
		updatedGroups[groupIndex].filters.splice(filterIndex, 1);
		setFilterGroups(updatedGroups);
	};

	const handleAddFilter = (groupIndex: number) => {
		setSelectedGroupIndex(groupIndex);
		setSelectedFilterIndex(null);
		setCurrentFilter(null);
	};

	const handleAddGroup = () => {
		setFilterGroups([...filterGroups, { filters: [] }]);
		setCurrentFilter(null);
	};

	const handleEditFilter = (groupIndex: number, filterIndex: number) => {
		const filterToEdit = filterGroups[groupIndex].filters[filterIndex];
		setCurrentFilter(filterToEdit);
		setSelectedGroupIndex(groupIndex);
		setSelectedFilterIndex(filterIndex);
	};

	const resetSelection = () => {
		setSelectedGroupIndex(null);
		setSelectedFilterIndex(null);
		setCurrentFilter(null);
	};

	const handleRemoveGroup = (groupIndex: number) => {
		if (filterGroups.length === 1) return; // Prevent removing the last group
		const updatedGroups = filterGroups.filter((_, idx) => idx !== groupIndex);
		setFilterGroups(updatedGroups);
		resetSelection();
	};

	return (
		<div className="d-flex">
			<div className="flex-grow-1 px-4 py-3 border-end">
				<Label><h5 className="fw-bold mb-2">{t("properties.auto_assign.assignment_conditions")}</h5></Label>
				<p className="text-muted small">
					{t("properties.auto_assign.assignment_conditions_helper_text1")}
				</p>

				{filterGroups.map((group, groupIdx) => (
					<div key={groupIdx} className="mt-4">
						{groupIdx > 0 && (
							<div className="text-center my-3">
								<span className="badge bg-light text-dark border px-3 py-2">
									{t("properties.auto_assign.if_previous_group_doesnt_match")}
								</span>
							</div>
						)}

						<div className="d-flex justify-content-between align-items-center mb-2">
							<h6 className="text-muted mb-0">{`${t("properties.auto_assign.condition_group")} ${groupIdx + 1}`}</h6>
							{filterGroups.length > 1 && (
								<button
									type="button"
									className="btn btn-sm btn-light border text-danger d-flex align-items-center gap-1"
									onClick={() => handleRemoveGroup(groupIdx)}
									title={t("properties.auto_assign.remove_group")}
								>
									<i className="ri-delete-bin-line" />
									<span className="d-none d-md-inline">{t("properties.auto_assign.remove_group")}</span>
								</button>
							)}
						</div>



						{group.filters.map((filter: any, filterIdx: number) => (
							<Card key={filterIdx} className="mb-2 border shadow-sm">
								<CardHeader className="d-flex justify-content-end gap-3 p-2">
									<i className="ri-edit-line text-primary cursor-pointer" onClick={() => handleEditFilter(groupIdx, filterIdx)} />
									<i className="ri-delete-bin-5-line text-danger cursor-pointer" onClick={() => handleRemoveFilter(groupIdx, filterIdx)} />
								</CardHeader>
								<CardBody className="bg-light">
									<span className="fw-medium">{filter.keyLabel}</span>&nbsp;
									<span>{filter.conditionLabel}</span>&nbsp;
									{filter.valuesLabel?.map((v: any, i: number) => (
										<span key={i} className="fw-semibold">
											{v.label}{i < filter.valuesLabel.length - 1 ? ', ' : ''}
										</span>
									))}
									{filter.value && filter.highValue && (
										<span className="fw-semibold"> {filter.value} to {filter.highValue}</span>
									)}
								</CardBody>
							</Card>
						))}

						<Card className="border mb-4 shadow-sm">
							<CardBody className="d-flex justify-content-between align-items-center py-2">
								<span className="text-muted">{t("properties.auto_assign.add_new_condition_to_this_group")}</span>
								<Button
									color="primary"
									size="sm"
									onClick={() => handleAddFilter(groupIdx)}
									disabled={selectedGroupIndex !== null}
								>
									<i className="ri-filter-3-line me-1" />
									{t("properties.auto_assign.add_condition")}
								</Button>
							</CardBody>
						</Card>
					</div>
				))}
				<div className="text-center mt-4">
					<Button color="primary" outline onClick={handleAddGroup} disabled={selectedGroupIndex !== null}>
						<i className="ri-play-list-add-line me-2" />
						{t("properties.auto_assign.add_another_condition_group")}
					</Button>
				</div>
			</div>

			{selectedGroupIndex !== null ? (
				<AddFilter
					filter={currentFilter}
					dataFields={dataFields}
					onFilterSelected={handleFilterSelection}
					selectedGroup={selectedGroupIndex}
					setSelectedGroupIndex={setSelectedGroupIndex}
				/>
			) : (
				<div className="w-50 bg-light d-flex align-items-center justify-content-center p-4">
					<h5 className="text-muted">{t("properties.auto_assign.click_add_condition_to_define_rule")}</h5>
				</div>
			)}
		</div>
	);
};

export default Filter;
